'use client';
import { createContext, useContext, useState } from 'react';
import { useRouter } from 'next/navigation';
import { buildQueryObject, TDFRequestQueryParams } from '@/lib/dataFrameApi';
import { SortColumn } from 'react-data-grid';
import isEqual from 'lodash-es/isEqual';
import { TKeyValuePair } from '@/types';

type TDataframeQueryContext = {
  query: TDFRequestQueryParams;
  nextQuery: TDFRequestQueryParams;
  setNextQuery: (nextQuery: TDFRequestQueryParams) => void;
  sortable: boolean;
};

const DataframeQueryContext = createContext<TDataframeQueryContext>({} as TDataframeQueryContext);

export const DataframeQueryContextProvider: React.FC<{
  searchParams: TKeyValuePair;
  children: React.ReactNode;
  sortable?: boolean;
}> = ({ searchParams, children, sortable = true }) => {
  const query = buildQueryObject(searchParams);
  const [nextQuery, setNextQuery] = useState<TDFRequestQueryParams>(query);

  const value = {
    query,
    nextQuery,
    setNextQuery,
    sortable,
  };

  return <DataframeQueryContext.Provider value={value}>{children}</DataframeQueryContext.Provider>;
};

type TSortParams = {
  columnKey?: string;
  direction?: string;
};

type TUseDataframeQueryContext = {
  setNextQuery: (nextQuery: TDFRequestQueryParams) => void;
  onPageChange: (pageNum: number, callback?: () => void) => void;
  onQueryIdChange: (queryId: string | undefined) => void;
  onSortChange: (sortCols: TSortParams[]) => void;
  onQueryColumnsChange: (columns: string[]) => void;
  onQueryClear: () => void;
  revalidating: boolean;
  sortColumns: SortColumn[];
};

const useDataframeQueryContext = (): TUseDataframeQueryContext => {
  const router = useRouter();
  const context = useContext(DataframeQueryContext);

  if (context === undefined) {
    console.error('useDataframeQueryContext was used outside of its Provider');
    throw new Error('useDataframeQueryContext was used outside of its Provider');
  }

  const { query, nextQuery, setNextQuery, sortable } = context;

  const onQueryChange = (updatedQuery: TDFRequestQueryParams) => {
    const pathName = window.location.pathname;
    const urlQuery = new URLSearchParams(updatedQuery as Record<string, string>).toString();
    router.push(`${pathName}?${urlQuery}`);
    router.refresh();
  };

  const onSortChange = (sortCols: TSortParams[]): void => {
    if (!sortable) {
      return;
    }
    const sort = sortCols[0];
    const columnKey = sort?.columnKey;
    const direction = sort?.direction;
    const reverse = direction ? (direction === 'DESC' ? 'true' : 'false') : undefined;

    const updatedQuery = buildQueryObject({
      ...query,
      sort_by: columnKey,
      reverse,
      page: undefined,
    });
    setNextQuery(updatedQuery);
    onQueryChange(updatedQuery);
  };

  const onPageChange = (pageNum: number, callback?: () => void) => {
    const updatedQuery = buildQueryObject({ ...query, page: pageNum.toString() });
    setNextQuery(updatedQuery);
    onQueryChange(updatedQuery);
    callback?.();
  };

  const onQueryIdChange = (queryId: string | undefined) => {
    const updatedQuery = buildQueryObject({
      ...query,
      query_id: queryId,
      page: undefined,
      sort_by: undefined,
      reverse: undefined,
      columns: undefined,
    });
    setNextQuery(updatedQuery);
    onQueryChange(updatedQuery);
  };

  const onQueryColumnsChange = (columns: string[]) => {
    const updatedQuery = buildQueryObject({ ...query, columns });
    setNextQuery(updatedQuery);
    onQueryChange(updatedQuery);
  };

  const onQueryClear = () => {
    setNextQuery({});
    onQueryChange({});
  };

  const validNextQuery = nextQuery && buildQueryObject(nextQuery);
  const revalidating = validNextQuery && !isEqual(validNextQuery, query);
  const sortDirection = query?.reverse && query?.reverse === 'true' ? 'DESC' : 'ASC';
  const sortColumn = query?.sort_by && ({ columnKey: query.sort_by, direction: sortDirection } as SortColumn);

  return {
    setNextQuery,
    onPageChange,
    onSortChange,
    onQueryIdChange,
    onQueryColumnsChange,
    onQueryClear,
    revalidating,
    sortColumns: sortColumn ? [sortColumn] : [],
  };
};

export default useDataframeQueryContext;
